<template>
    <div>
        <div class="white-box md:mb-5 pb-12 md:pb-6">
            <transition
                name="opacity"
                mode="out-in"
            >
                <Skeletons v-if="isLoadingData" />
                <div v-else>
                    <ul class="text-tip pb-2.5 border-b border-gray-200">
                        <li>指定生效後，本契約各項保險給付，有未指定者，得逕予匯入同一人已指定之帳戶。</li>
                    </ul>
                    <div
                        v-for="(object , idx) in infoArray"
                        v-show="object.beneFiBankAccountList.length !== 0"
                        :key="idx"
                        class="mt-3.5 md:mt-5 first:mt-0 border-b border-gray-200 last:border-0"
                    >
                        <h4>{{ object.payCodeName }}</h4>
                        <div class="divide-y divide-gray-100 md:divide-dashed">
                            <PolicyAccount
                                v-for="(item, i) in object.beneFiBankAccountList"
                                :key="i"
                                v-model:isOpenChange="item.isOpenChange"
                                v-model:validInput="item.validInput"
                                :dataBase="item.showInfo"
                                :data="item.userInfo"
                                :bankOptions="item.bankOptions"
                                :branchOptions="item.branchOptions"
                                class="pt-3.5"
                                @getBranchOptions="headBankNo => getBranchOptions(headBankNo,item)"
                                @getValidResult="getValidResult"
                            >
                                <template #subtitle>
                                    <div class="pb-2.5 text-base md:text-lg text-black font-bold border-bottom">
                                        受益人：{{ item.showInfo.beneFiName }} {{ item.showInfo.beneFiId || '' }}
                                    </div>
                                </template>
                            </PolicyAccount>
                        </div>
                    </div>
                </div>
            </transition>
        </div>
        <Bottombar>
            <template #right>
                <ButtonNextService
                    v-model:isSubmitOk="isSubmitOk"
                    :disabled="!isDisabled"
                    @click="validAllInput()"
                />
            </template>
        </Bottombar>
        <Loading :is-loading="isLoading" />
        <PopupBase v-model:visiblePopup="visiblePopup">
            <template #title>
                請調整資料格式
            </template>
            <template #content>
                <ul
                    v-if="errorResponse.length>0"
                    class="space-y-2 list-decimal ml-6"
                >
                    <li
                        v-for="(item, index) in errorResponse"
                        :key="index"
                    >
                        <p>
                            {{ `${fieldNameList[item.field]}${item.value || ''}` }}
                        </p>
                        <p class="text-error text-sm">
                            {{ item.errorMessage }}
                        </p>
                    </li>
                </ul>
            </template>
            <template #action>
                <button
                    class="btn gradient-primary rounded-full"
                    @click="closePopup"
                >
                    確定
                </button>
            </template>
        </PopupBase>
    </div>
</template>

<script>
import { computed, inject, reactive, ref, toRefs, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { getBankInfoB6, getBranchBankInfoB6, getInfoB6, submitB6 } from '@/apis/B6';
import Skeletons from './Skeletons.vue';
import PolicyAccount from '@/components/template/PolicyAccount.vue';
import Bottombar from '@/components/layout/Bottombar.vue';
import ButtonNextService from '@/components/buttons/ButtonNextService.vue';
import dialogContnet from '@/json/dialogContent.js';
import Loading from '@/components/Loading.vue';
import PopupBase from '@/components/dialogs/Popup.vue';
import _ from 'lodash';
import { openPopupCatchMessage } from '@/utils/vfmUtils';

export default {
    components: {
        Skeletons,
        PolicyAccount,
        Bottombar,
        ButtonNextService,
        Loading,
        PopupBase,
    },
    setup() {
        const $vfm = inject('$vfm');
        const route = useRoute();
        const store = useStore();
        const { code } = route.meta;
        const state = reactive({
            isLoading: false,
            isLoadingData: false,
            isSubmitOk: false,
            visiblePopup: false,
        });
        const data = reactive({
            infoArray: [],
            validResult: [],
            errorResponse: [],
        });
        const isDisabled = computed(() => data.infoArray.some(i => i.beneFiBankAccountList.some(j => j.isOpenChange)));
        const validCount = ref(0);
        const validFinished = computed(() => validCount.value === 0);
        const result = computed(() => store.state.services.changeItemsResult[code]);
        const fieldNameList = {
            headBankNo: '銀行總行代號',
            branchBankNo: '銀行分行代號',
            bankAccount: '銀行帳戶',
            bankAccountName: '銀行帳戶名',
            payCode: '給付別',
        };

        const getBranchOptions = (headBankNo, item) => {
            getBranchBankInfoB6(headBankNo).then((res) => {
                if (res.data) {
                    item.branchOptions = res.data.branchBankInfoList.map(i => {
                        i.value = i.bankNo;
                        i.label = `${i.bankNo} ${i.bankName}`;
                        return i;
                    });
                }
            });
        };

        const getData = async () => {
            state.isLoadingData = true;
            let bankOptions = [];
            await getBankInfoB6().then((res) => {
                if (res.data) {
                    bankOptions = res.data.bankInfoList.map(i => {
                        i.value = i.bankNo;
                        i.label = `${i.bankNo} ${i.bankName}`;
                        return i;
                    });
                }
            });
            getInfoB6().then((res) => {
                if (res.data) {
                    data.infoArray = res.data.beneFiPayCodeList.map(i => {
                        i.beneFiBankAccountList = i.beneFiBankAccountList.map(j => {
                            const obj = {};
                            obj.isOpenChange = false;
                            obj.validInput = false;
                            obj.showInfo = j;
                            obj.showInfo.isForeignAccount = res.data.foreignAccount;
                            obj.userInfo = {
                                payCode: i.payCode,
                                headBankNo: '',
                                branchBankNo: '',
                                bankAccount: '',
                                bankAccountName: '',
                                uuid: j.uuid,
                                errorMessage: {
                                    headBankNo: '',
                                    branchBankNo: '',
                                    bankAccount: '',
                                    bankAccountName: '',
                                    beneFiId: '',
                                },
                            };
                            obj.bankOptions = bankOptions;
                            obj.branchOptions = [];
                            return obj;
                        });
                        return i;
                    });
                }
            }).finally(() => {
                state.isLoadingData = false;
            });
        };
        if (!result.value) {
            getData();
        } else {
            data.infoArray = _.cloneDeep(result.value.infoArray);
        }

        const validAllInput = () => {
            data.infoArray.forEach(i => {
                if (i.beneFiBankAccountList.length > 0) {
                    i.beneFiBankAccountList.forEach(j => {
                        if (j.isOpenChange) {
                            j.validInput = true;
                            validCount.value++;
                        }
                    });
                }
            });
        };

        const getValidResult = (hasError, userData) => {
            state.isLoading = true;
            const obj = {};
            obj.hasError = hasError;
            obj.userData = _.cloneDeep(userData);
            delete obj.userData.errorMessage;
            data.validResult.push(obj);
            validCount.value--;
        };

        const submit = (returnData) => {
            submitB6(returnData).then(() => {
                const storeData = {};
                storeData.infoArray = data.infoArray;
                store.dispatch('services/setChangeItemsResult', {
                    code,
                    value: storeData,
                });
                state.isSubmitOk = true;
            }).catch((err) => {
                state.isLoading = false;
                const errData = err.data?.details || [];
                if (errData[0]?.field) {
                    data.errorResponse = errData;
                    state.visiblePopup = true;
                } else {
                    openPopupCatchMessage(err);
                }
            }).finally(() => {
                state.isLoading = false;
                data.validResult = [];
            });
        };

        watch(validFinished, (nV) => {
            if (nV) {
                if (data.validResult.some(i => i.hasError)) {
                    state.isLoading = false;
                    data.validResult = [];
                    modalShow(dialogContnet.validateMsg);
                } else {
                    const returnData = {};
                    returnData.bankAccountList = data.validResult.map(i => i.userData);
                    submit(returnData);
                }
            }
        });

        const modalShow = (params) => {
            $vfm.show({
                component: 'DialogBase',
                params,
                on: {
                    confirm() {
                        $vfm.hide('DialogBase');
                    },
                    closed() {
                        $vfm.hide('DialogBase');
                    },
                },
            });
        };

        const closePopup = () => {
            state.visiblePopup = false;
            data.errorResponse = [];
        };

        return {
            ...toRefs(state),
            ...toRefs(data),
            isDisabled,
            fieldNameList,
            closePopup,
            validAllInput,
            getBranchOptions,
            getValidResult,
        };
    },

};
</script>
<style lang="scss" scoped>
.border-style {
    @apply md:border-dashed last:border-gray-200 last:border-solid;
}
</style>
